import React, {useState, useEffect, useRef} from 'react'
import GenericTable from '../../shared/generic-table'
import '../../../styles/control-center-styles.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import advanced from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import {toast} from 'react-toastify'
import {useAuthClient} from '../../../utils/use-auth-client'
import {getUserRoles} from '../../../utils/get-user-roles'

dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advanced)

const DEFAULT_TIMEZONE = 'America/Chicago'
const formatDate = (dateStr, timezone = DEFAULT_TIMEZONE) => {
  try {
    if (!dateStr) return 'N/A'
    const date = dayjs(dateStr)
    return date.isValid()
      ? date.tz(timezone).format('MM/DD/YYYY HH:mmA z')
      : 'Invalid date'
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Date error'
  }
}

// Custom hook for debounced value
const useDebouncedValue = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

const CompletedOrderModal = ({order, open, onClose}) => {
  // Don't render anything if not open
  if (!open) {
    return null
  }

  // Add a check for order data
  if (!order) {
    console.error('No order data provided to modal')
    return null
  }

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <section className="appointment-info-modal">
        <div className="appointment-modal-header">
          <h2>Order Details</h2>
          <button onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="appointment-info-content">
          {order && (
            <div className="appointment-details-container">
              <div className="appointment-details-grid">
                <div className="appointment-detail">
                  <span className="detail-label">Order Completed</span>
                  <span className="detail-value">
                    {formatDate(order.order_completed_date)}
                  </span>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Client Email</span>
                  <span className="detail-value">
                    {order.client_email || 'N/A'}
                  </span>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Thryv ID</span>
                  <span className="detail-value">
                    {order.thryv_id || 'N/A'}
                  </span>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Company Name</span>
                  <span className="detail-value">
                    {order.company_name || 'N/A'}
                  </span>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Order Total</span>
                  <span className="detail-value">
                    ${parseFloat(order.order_total || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

const CompletedOrders = ({
  keapPartnerInfo,
  setDashboardLoading,
  dashboardLoading,
  title,
  userData,
}) => {
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [countPerPage] = useState(5)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
  const dateRangeRef = useRef(null)
  const client = useAuthClient()

  // Check if user has InfluencerOps role - supports both Auth0 and Okta
  const userRoles = getUserRoles(userData)
  const isInfluencerOps = userRoles.includes('InfluencerOps')

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  })

  // Consolidated filter state
  const [filters, setFilters] = useState({
    influencerId: '',
    orderId: '',
    companyName: '',
    clientEmail: '',
    thryvId: '',
    startDate: '',
    endDate: '',
  })

  // Debounced filter values for API calls
  const debouncedInfluencerId = useDebouncedValue(filters.influencerId)
  const debouncedOrderId = useDebouncedValue(filters.orderId)
  const debouncedCompanyName = useDebouncedValue(filters.companyName)
  const debouncedClientEmail = useDebouncedValue(filters.clientEmail)
  const debouncedThryvId = useDebouncedValue(filters.thryvId)
  const debouncedStartDate = useDebouncedValue(filters.startDate)
  const debouncedEndDate = useDebouncedValue(filters.endDate)

  // Filter update handlers
  const updateFilter = (key, value) => {
    setFilters(prev => {
      const updated = {...prev, [key]: value}

      // If start date is greater than end date, reset end date
      if (key === 'startDate' && prev.endDate && value > prev.endDate) {
        updated.endDate = ''
      }

      return updated
    })
  }

  const clearAllFilters = () => {
    setFilters({
      influencerId: '',
      orderId: '',
      companyName: '',
      clientEmail: '',
      thryvId: '',
      startDate: '',
      endDate: '',
    })
    setIsDateRangeOpen(false)
    setPage(1)
  }

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        isDateRangeOpen &&
        dateRangeRef.current &&
        !dateRangeRef.current.contains(event.target)
      ) {
        setIsDateRangeOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDateRangeOpen])

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [
    debouncedInfluencerId,
    debouncedOrderId,
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
  ])

  useEffect(() => {
    const fetchCompletedOrders = async () => {
      setIsLoading(true)
      setDashboardLoading(true)
      try {
        // Build URL with query parameters
        const params = new URLSearchParams({
          page: page.toString(),
          countPerPage: countPerPage.toString(),
        })

        if (debouncedInfluencerId)
          params.append('influencerId', debouncedInfluencerId)
        if (debouncedOrderId) params.append('orderId', debouncedOrderId)
        if (debouncedCompanyName)
          params.append('companyName', debouncedCompanyName)
        if (debouncedClientEmail)
          params.append('clientEmail', debouncedClientEmail)
        if (debouncedThryvId) params.append('thryvId', debouncedThryvId)
        if (debouncedStartDate) params.append('startDate', debouncedStartDate)
        if (debouncedEndDate) params.append('endDate', debouncedEndDate)

        const url = `keappartner/fetchKeapCompletedOrder/${keapPartnerInfo.keapPartnerId}?${params}`
        const data = await client(url)

        if (data.success && data.data && Array.isArray(data.data.data)) {
          setRows(data.data.data)
          if (data.data.pagination) {
            setPagination({
              page: data.data.pagination.page,
              totalPages: data.data.pagination.totalPages,
              total: data.data.pagination.total,
              hasNext: data.data.pagination.hasNext,
              hasPrev: data.data.pagination.hasPrev,
            })
          }
        }
      } catch (error) {
        console.error('Error fetching completed orders:', error)
        toast.error('Error fetching completed orders')
        setRows([])
      } finally {
        setIsLoading(false)
        setDashboardLoading(false)
      }
    }

    fetchCompletedOrders()
  }, [
    page,
    countPerPage,
    debouncedInfluencerId,
    debouncedOrderId,
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
    setDashboardLoading,
    client,
    keapPartnerInfo.keapPartnerId,
  ])

  const handleDateRangeChange = value => {
    if (Array.isArray(value)) {
      const [start, end] = value
      const startStr = start ? dayjs(start).format('YYYY-MM-DD') : ''
      const endStr = end ? dayjs(end).format('YYYY-MM-DD') : ''

      updateFilter('startDate', startStr)
      updateFilter('endDate', endStr)

      if (start && end) {
        setIsDateRangeOpen(false)
      }
    }
  }

  const dateRangeLabel = filters.startDate
    ? filters.endDate
      ? `${dayjs(filters.startDate).format('MM/DD/YYYY')} - ${dayjs(
          filters.endDate,
        ).format('MM/DD/YYYY')}`
      : `${dayjs(filters.startDate).format('MM/DD/YYYY')} - Select end date`
    : ''

  const getCalendarValue = () => {
    if (!filters.startDate && !filters.endDate) return null

    const toLocalDate = dateStr => {
      if (!dateStr) return null
      const [year, month, day] = dateStr.split('-').map(Number)
      return new Date(year, month - 1, day)
    }

    return [toLocalDate(filters.startDate), toLocalDate(filters.endDate)]
  }

  const baseColumns = [
    {key: 'order_id', label: 'Order ID'},
    {
      key: 'order_completed_date',
      label: 'Completed Date',
      render: (_, row) => (
        <p className="formmated-date">{formatDate(row.order_completed_date)}</p>
      ),
    },
    {
      key: 'client_email',
      label: 'Client Email',
    },
    {
      key: 'thryv_id',
      label: 'Thryv ID',
    },
    {
      key: 'company_name',
      label: 'Company Name',
    },
    {
      key: 'order_total',
      label: 'Total',
      render: (_, row) => `$${parseFloat(row.order_total || 0).toFixed(2)}`,
    },
  ]

  // Add Influencer Name column only for InfluencerOps users
  const influencerNameColumn = isInfluencerOps
    ? {
        key: 'influencer_name',
        label: 'Influencer Name',
        render: name => name || 'N/A',
      }
    : null

  const actionColumn = {
    key: 'actions',
    label: 'Actions',
    render: (_, row) => (
      <button
        className="table-button"
        onClick={e => {
          e.stopPropagation()
          setSelectedOrder(row)
        }}
      >
        Details
      </button>
    ),
  }

  const columns = influencerNameColumn
    ? [...baseColumns, influencerNameColumn, actionColumn]
    : [...baseColumns, actionColumn]

  return (
    <>
      <div style={{padding: 16}}>
        <h3
          className="control-center-item-header"
          style={{marginBottom: '8px'}}
        >
          {title}
        </h3>
        {rows.length > 0 && (
          <h4
            className="control-center-display-text"
            style={{marginBottom: '16px'}}
          >
            Displaying {page * countPerPage - countPerPage + 1}-
            {Math.min(page * countPerPage, pagination.total)} out of{' '}
            {pagination.total}
          </h4>
        )}
        <div
          style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            border: '1px solid #e9e9e9',
          }}
        >
          {isInfluencerOps && (
            <div style={{flex: '1 1 auto', minWidth: '200px'}}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  marginBottom: '6px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  color: '#666',
                  letterSpacing: '0.5px',
                }}
              >
                Influencer ID
              </label>
              <input
                type="text"
                placeholder="Enter influencer ID to filter"
                value={filters.influencerId}
                onChange={e => updateFilter('influencerId', e.target.value)}
                style={{
                  padding: '10px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d4d4d4',
                  fontSize: '14px',
                  fontFamily: 'Open Sans, sans-serif',
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff',
                  color: '#231f20',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => (e.target.style.borderColor = '#fa6000')}
                onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
              />
            </div>
          )}
          <div style={{flex: '1 1 auto', minWidth: '180px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Order ID
            </label>
            <input
              type="text"
              placeholder="Enter order ID to filter"
              value={filters.orderId}
              onChange={e => updateFilter('orderId', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
          </div>
          <div style={{flex: '1 1 auto', minWidth: '200px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Company Name
            </label>
            <input
              type="text"
              placeholder="Enter company name to filter"
              value={filters.companyName}
              onChange={e => updateFilter('companyName', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
          </div>
          <div style={{flex: '1 1 auto', minWidth: '220px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Client Email
            </label>
            <input
              type="text"
              placeholder="Enter client email to filter"
              value={filters.clientEmail}
              onChange={e => updateFilter('clientEmail', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
          </div>
          <div style={{flex: '1 1 auto', minWidth: '180px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Thryv ID
            </label>
            <input
              type="text"
              placeholder="Enter Thryv ID to filter"
              value={filters.thryvId}
              onChange={e => updateFilter('thryvId', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
          </div>
          <div style={{flex: '1 1 auto', minWidth: '260px'}} ref={dateRangeRef}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Date Range
            </label>
            <div style={{position: 'relative'}}>
              <input
                type="text"
                readOnly
                value={dateRangeLabel}
                placeholder="Select date range"
                onClick={() => setIsDateRangeOpen(prev => !prev)}
                style={{
                  padding: '10px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d4d4d4',
                  fontSize: '14px',
                  fontFamily: 'Open Sans, sans-serif',
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff',
                  color: '#231f20',
                  transition: 'border-color 0.2s',
                  cursor: 'pointer',
                }}
                onFocus={e => (e.target.style.borderColor = '#fa6000')}
                onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
              />
              {isDateRangeOpen && (
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 20,
                    marginTop: '8px',
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <Calendar
                    selectRange
                    onChange={handleDateRangeChange}
                    value={getCalendarValue()}
                  />
                </div>
              )}
            </div>
          </div>
          <div style={{flex: '0 0 auto', minWidth: '120px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: 'transparent',
                letterSpacing: '0.5px',
              }}
            >
              &nbsp;
            </label>
            <button
              onClick={clearAllFilters}
              style={{
                padding: '10px 16px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#fa6000',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#fa6000'
                e.target.style.color = '#ffffff'
                e.target.style.borderColor = '#fa6000'
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = '#ffffff'
                e.target.style.color = '#fa6000'
                e.target.style.borderColor = '#d4d4d4'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      {rows.length > 0 && (
        <div style={{padding: 16}}>
          <GenericTable
            columns={columns}
            rows={rows}
            emptyMessage="No completed orders found."
            isLoading={isLoading}
            loadingMessage="Loading completed orders..."
            pagination={{
              page: pagination.page,
              totalPages: pagination.totalPages,
              hasPrev: pagination.hasPrev,
              hasNext: pagination.hasNext,
              onPageChange: setPage,
            }}
          />

          {selectedOrder && (
            <CompletedOrderModal
              order={selectedOrder}
              open={true}
              onClose={() => setSelectedOrder(null)}
            />
          )}
        </div>
      )}

      {!(rows.length > 0) && !dashboardLoading && (
        <div style={{padding: 16}}>
          <div
            style={{
              textAlign: 'center',
              paddingTop: '60px',
              paddingBottom: '60px',
            }}
          >
            <h4
              style={{
                color: '#808080',
                fontSize: '16px',
                fontWeight: '600',
              }}
            >
              No completed orders found
            </h4>
            <p style={{color: '#999', fontSize: '14px', marginTop: '8px'}}>
              Try adjusting your filters to see more results
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default CompletedOrders
